import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Avatar, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../routes';
import linxLogo from 'assets/img/linx.png'
import { me } from 'api/usersApi';
import AccountMenu from 'components/AccountMenu';

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
);

function DrawerLayoutContent(props) {
  let navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [currentUser, setCurrentUser] = useState(null)
  const [menuAnchorElement, setMenuAnchorElement] = useState(null);
  const menuOpen = Boolean(menuAnchorElement);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const getMe = async () => {
    const response = await me()
    setCurrentUser(response.data)
  }

  useEffect(() => {
    if (!currentUser) {
      getMe()
    } else {
      unauthorized && navigate("/401")
    }
  }, [currentUser, window.location.pathname])

  const drawerRoutes = routes.filter((route) =>
  route.showInSideDrawer && (!route.adminRestricted || currentUser?.role === 'super_admin'))
  const activeRoute = drawerRoutes.find((route) => route.path === window.location.pathname) || {}
  const accessedRoute = routes.find((route) => route.path === window.location.pathname) || {}
  const unauthorized = accessedRoute.adminRestricted && currentUser?.role !== 'super_admin'
  
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="absolute" open={open}>
        <Toolbar
          sx={{
            pr: '24px', // keep right padding when drawer closed
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: '36px',
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {activeRoute.title}
          </Typography>
          <IconButton color="inherit" onClick={(e) => setMenuAnchorElement(e.currentTarget)}>
            <Avatar
            sx={{ bgcolor: 'white', color: 'primary.main' }}
            >
              <PersonIcon />
            </Avatar>
          </IconButton>
        </Toolbar>
        <AccountMenu
          anchorEl={menuAnchorElement}
          open={menuOpen}
          onClose={() => setMenuAnchorElement(null)}
        />
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: [1],
          }}
        >
          <img src={linxLogo} alt='linx' style={{ maxHeight: '40px', width: 'auto', marginLeft: 20 }} />
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        <List component="nav">
          {
            drawerRoutes.map((route, key) => <ListItemButton key={route.path+key} onClick={() => navigate(route.path)}>
              <ListItemIcon>
                {route.icon}
                </ListItemIcon>
              <ListItemText primary={route.title} />
            </ListItemButton>)
            }
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          {props.children}
        </Container>
      </Box>
    </Box>
  );
}

export default function DrawerLayout(props) {
  return <DrawerLayoutContent {...props} />;
}